'use client';

import DOMPurify from 'dompurify';
import { diffLines, diffWordsWithSpace } from 'diff';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { gfm as turndownGfm } from 'turndown-plugin-gfm';

export type MarkdownLinkKind = 'inline' | 'reference' | 'image' | 'autolink' | 'bare-url';

export type MarkdownLinkEntry = {
  text: string;
  url: string;
  type: MarkdownLinkKind;
};

export type MarkdownCounts = {
  words: number;
  characters: number;
  charactersNoSpaces: number;
  headings: number;
  links: number;
  listItems: number;
};

export type MarkdownDiffChunk = {
  type: 'added' | 'removed' | 'unchanged';
  text: string;
};

export type MarkdownDiffLine = {
  type: 'added' | 'removed' | 'unchanged';
  chunks: MarkdownDiffChunk[];
};

export type TableAlignment = 'left' | 'center' | 'right';

export type ReadmeSections = {
  projectName: string;
  shortDescription: string;
  features: string;
  installation: string;
  usage: string;
  configuration: string;
  scripts: string;
  license: string;
  links: string;
};

marked.setOptions({
  gfm: true,
  breaks: false,
});

function sanitizeHtml(html: string) {
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
  });
}

function escapeHtml(value: string) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function normalizeLineEndings(value: string) {
  return value.replace(/\r\n?/g, '\n');
}

function trimTrailingWhitespace(value: string) {
  return normalizeLineEndings(value)
    .split('\n')
    .map((line) => line.replace(/[ \t]+$/g, ''))
    .join('\n');
}

function detectGridDelimiter(lines: string[]) {
  if (lines.some((line) => line.includes('\t'))) {
    return '\t';
  }

  if (lines.some((line) => /\|/.test(line))) {
    return '|';
  }

  return ',';
}

function parseGridRows(raw: string, columnCount?: number) {
  const lines = normalizeLineEndings(raw)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    throw new Error('Paste at least one row of table data first.');
  }

  const delimiter = detectGridDelimiter(lines);
  const rows = lines.map((line) => {
    let columns =
      delimiter === '|'
        ? line
            .split('|')
            .map((part) => part.trim())
            .filter(
              (part, index, parts) =>
                !(parts.length > 2 && index === 0 && part === '') &&
                !(parts.length > 2 && index === parts.length - 1 && part === ''),
            )
        : line.split(delimiter).map((part) => part.trim());

    if (columnCount) {
      columns = [...columns.slice(0, columnCount)];
      while (columns.length < columnCount) {
        columns.push('');
      }
    }

    return columns;
  });

  return rows;
}

function normalizeTableAlignmentRow(cellCount: number, alignments: TableAlignment[]) {
  return `| ${new Array(cellCount)
    .fill(null)
    .map((_, index) => {
      switch (alignments[index] ?? 'left') {
        case 'center':
          return ':---:';
        case 'right':
          return '---:';
        case 'left':
        default:
          return ':---';
      }
    })
    .join(' | ')} |`;
}

function padCell(value: string, width: number, alignment: TableAlignment) {
  const content = value.trim();
  const gap = Math.max(width - content.length, 0);

  if (alignment === 'right') {
    return `${' '.repeat(gap)}${content}`;
  }

  if (alignment === 'center') {
    const left = Math.floor(gap / 2);
    const right = gap - left;
    return `${' '.repeat(left)}${content}${' '.repeat(right)}`;
  }

  return `${content}${' '.repeat(gap)}`;
}

function tableRowsToMarkdown(
  rows: string[][],
  hasHeaderRow: boolean,
  alignments: TableAlignment[],
) {
  const width = Math.max(...rows.map((row) => row.length));
  const normalized = rows.map((row) => {
    const next = [...row];
    while (next.length < width) {
      next.push('');
    }
    return next;
  });
  const columnWidths = new Array(width)
    .fill(0)
    .map((_, index) => Math.max(...normalized.map((row) => row[index].trim().length), 3));

  const formatRow = (row: string[]) =>
    `| ${row
      .map((cell, index) => padCell(cell, columnWidths[index], alignments[index] ?? 'left'))
      .join(' | ')} |`;

  const lines: string[] = [];
  const header = normalized[0];
  lines.push(formatRow(header));
  lines.push(normalizeTableAlignmentRow(width, alignments));

  const bodyRows = hasHeaderRow ? normalized.slice(1) : normalized;
  for (const row of bodyRows) {
    lines.push(formatRow(row));
  }

  return lines.join('\n');
}

export async function renderMarkdownToHtml(markdown: string) {
  const rawHtml = await marked.parse(normalizeLineEndings(markdown));
  return sanitizeHtml(rawHtml);
}

export async function renderMarkdownPreview(markdown: string) {
  const html = await renderMarkdownToHtml(markdown);
  return {
    html,
    counts: countMarkdownContent(markdown),
  };
}

export function convertHtmlToMarkdown(html: string) {
  const turndown = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-',
  });
  turndown.use(turndownGfm);

  return normalizeLineEndings(turndown.turndown(html)).trim();
}

export function cleanMarkdown(markdown: string) {
  const lines = normalizeLineEndings(markdown).split('\n');
  const normalized: string[] = [];
  let previousBlank = false;
  let inFence = false;

  for (let index = 0; index < lines.length; index += 1) {
    let line = lines[index];
    const isFence = /^\s*(```|~~~)/.test(line);

    // Inside a fenced block every character is content: no heading fix, no list
    // fix, no whitespace trimming, no blank-line collapsing.
    if (inFence && !isFence) {
      normalized.push(line);
      previousBlank = false;
      continue;
    }

    if (isFence) {
      if (!inFence && normalized.length && normalized[normalized.length - 1] !== '') {
        normalized.push('');
      }
      normalized.push(line.replace(/[ \t]+$/g, ''));
      inFence = !inFence;
      previousBlank = false;
      continue;
    }

    // Two trailing spaces are a hard line break in Markdown, so keep them.
    const hardBreak = /\S {2,}$/.test(line);
    line = line.replace(/[ \t]+$/g, '');

    if (/^#{1,6}\S/.test(line)) {
      line = line.replace(/^(#{1,6})(\S)/, '$1 $2');
    }

    if (/^(\s*[-*+]|\s*\d+\.)\S/.test(line)) {
      line = line.replace(/^(\s*[-*+]|\s*\d+\.)(\S)/, '$1 $2');
    }

    const isBlank = line.trim().length === 0;
    if (isBlank) {
      if (previousBlank) {
        continue;
      }
      previousBlank = true;
      normalized.push('');
      continue;
    }

    previousBlank = false;
    normalized.push(hardBreak ? `${line}  ` : line);
  }

  return normalized.join('\n').trim();
}

export function extractMarkdownLinks(markdown: string): MarkdownLinkEntry[] {
  const source = normalizeLineEndings(markdown);
  const claimed: Array<[number, number]> = [];
  const output: MarkdownLinkEntry[] = [];

  // Patterns overlap by design: an image is also an inline link, and an inline
  // link contains a bare URL. Claim the characters a match consumed so the same
  // link is not counted two or three times.
  const overlaps = (start: number, end: number) =>
    claimed.some(([from, to]) => start < to && end > from);

  const collect = (
    pattern: RegExp,
    type: MarkdownLinkEntry['type'],
    read: (match: RegExpMatchArray) => { text: string; url: string },
  ) => {
    for (const match of source.matchAll(pattern)) {
      const start = match.index ?? 0;
      const end = start + match[0].length;
      if (overlaps(start, end)) {
        continue;
      }
      claimed.push([start, end]);
      output.push({ ...read(match), type });
    }
  };

  collect(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, 'image', (m) => ({
    text: m[1] || '(image)',
    url: m[2],
  }));
  collect(/\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, 'inline', (m) => ({
    text: m[1],
    url: m[2],
  }));
  collect(/^\[([^\]]+)\]:\s*(\S+)/gm, 'reference', (m) => ({ text: m[1], url: m[2] }));
  collect(/<((?:https?:\/\/|mailto:)[^>]+)>/g, 'autolink', (m) => ({ text: m[1], url: m[1] }));
  collect(/(^|[\s(])(https?:\/\/[^\s)<]+)(?=$|[\s)])/gm, 'bare-url', (m) => ({
    text: m[2],
    url: m[2],
  }));

  const seen = new Set<string>();
  return output.filter((entry) => {
    const key = `${entry.type}::${entry.text}::${entry.url}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function countMarkdownContent(markdown: string): MarkdownCounts {
  const normalized = normalizeLineEndings(markdown);
  const textOnly = normalized
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]+`/g, ' ')
    .replace(/!\[[^\]]*\]\(([^)]+)\)/g, ' ')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/<https?:\/\/[^>]+>/g, ' ')
    .replace(/[#>*_\-\[\]()]/g, ' ');
  const words = textOnly.trim().split(/\s+/).filter(Boolean).length;

  return {
    words,
    characters: normalized.length,
    charactersNoSpaces: normalized.replace(/\s+/g, '').length,
    headings: (normalized.match(/^#{1,6}\s+/gm) ?? []).length,
    links: extractMarkdownLinks(normalized).length,
    listItems: (normalized.match(/^\s*(?:[-*+]|\d+\.)\s+/gm) ?? []).length,
  };
}

export function generateMarkdownChecklist(items: string, checked = false) {
  const entries = normalizeLineEndings(items)
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

  if (!entries.length) {
    throw new Error('Add at least one checklist item first.');
  }

  return entries.map((item) => `- [${checked ? 'x' : ' '}] ${item}`).join('\n');
}

export function generateMarkdownTable(
  raw: string,
  columnCount: number,
  hasHeaderRow: boolean,
  alignments: TableAlignment[],
) {
  const rows = parseGridRows(raw, columnCount);

  if (!rows.length) {
    throw new Error('Paste table rows first.');
  }

  if (!hasHeaderRow) {
    rows.unshift(new Array(columnCount).fill('').map((_, index) => `Column ${index + 1}`));
  }

  return tableRowsToMarkdown(rows, true, alignments);
}

export function formatMarkdownTable(markdown: string) {
  const lines = normalizeLineEndings(markdown)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2 || !lines.every((line) => line.includes('|'))) {
    throw new Error('Paste one valid Markdown table with at least a header and separator row.');
  }

  const rows = lines.map((line) =>
    line
      .split('|')
      .map((part) => part.trim())
      .filter(
        (part, index, parts) =>
          !(parts.length > 2 && index === 0 && part === '') &&
          !(parts.length > 2 && index === parts.length - 1 && part === ''),
      ),
  );

  const header = rows[0];
  const separator = rows[1];
  const body = rows.slice(2);

  if (header.length < 2 || separator.length !== header.length) {
    throw new Error('The table header and separator row do not line up.');
  }

  const alignments = separator.map<TableAlignment>((cell) => {
    const trimmed = cell.trim();
    if (/^:-+:$/.test(trimmed)) {
      return 'center';
    }
    if (/^-+:$/.test(trimmed)) {
      return 'right';
    }
    return 'left';
  });

  return tableRowsToMarkdown([header, ...body], true, alignments);
}

export function buildReadmeMarkdown(sections: ReadmeSections) {
  const parts: string[] = [];

  if (sections.projectName.trim()) {
    parts.push(`# ${sections.projectName.trim()}`);
  }

  if (sections.shortDescription.trim()) {
    parts.push(sections.shortDescription.trim());
  }

  const sectionMap: Array<[string, string]> = [
    ['Features', sections.features],
    ['Installation', sections.installation],
    ['Usage', sections.usage],
    ['Configuration', sections.configuration],
    ['Scripts / Commands', sections.scripts],
    ['License', sections.license],
    ['Links', sections.links],
  ];

  for (const [title, value] of sectionMap) {
    const trimmed = value.trim();
    if (!trimmed) {
      continue;
    }

    parts.push(`## ${title}\n\n${trimmed}`);
  }

  return parts.join('\n\n').trim();
}

export function createMarkdownDiff(left: string, right: string) {
  const lineChanges = diffLines(normalizeLineEndings(left), normalizeLineEndings(right));
  const lines: MarkdownDiffLine[] = [];
  const summary = {
    addedLines: 0,
    removedLines: 0,
    changedBlocks: 0,
  };

  for (let index = 0; index < lineChanges.length; index += 1) {
    const part = lineChanges[index];

    if (part.added) {
      summary.addedLines += part.count ?? part.value.split('\n').filter(Boolean).length;
      summary.changedBlocks += 1;
      lines.push({
        type: 'added',
        chunks: [{ type: 'added', text: part.value }],
      });
      continue;
    }

    if (part.removed) {
      const next = lineChanges[index + 1];
      summary.removedLines += part.count ?? part.value.split('\n').filter(Boolean).length;
      summary.changedBlocks += 1;

      if (next?.added) {
        const wordChanges = diffWordsWithSpace(part.value, next.value);
        const removedChunks: MarkdownDiffChunk[] = [];
        const addedChunks: MarkdownDiffChunk[] = [];

        for (const chunk of wordChanges) {
          if (chunk.added) {
            addedChunks.push({ type: 'added', text: chunk.value });
          } else if (chunk.removed) {
            removedChunks.push({ type: 'removed', text: chunk.value });
          } else {
            removedChunks.push({ type: 'unchanged', text: chunk.value });
            addedChunks.push({ type: 'unchanged', text: chunk.value });
          }
        }

        lines.push({ type: 'removed', chunks: removedChunks });
        lines.push({ type: 'added', chunks: addedChunks });
        summary.addedLines += next.count ?? next.value.split('\n').filter(Boolean).length;
        index += 1;
      } else {
        lines.push({
          type: 'removed',
          chunks: [{ type: 'removed', text: part.value }],
        });
      }
      continue;
    }

    lines.push({
      type: 'unchanged',
      chunks: [{ type: 'unchanged', text: part.value }],
    });
  }

  const summaryText = [
    `Added lines: ${summary.addedLines}`,
    `Removed lines: ${summary.removedLines}`,
    `Changed blocks: ${summary.changedBlocks}`,
  ].join('\n');

  return {
    lines,
    summary,
    summaryText,
  };
}

export function renderMarkdownDiffHtml(diff: ReturnType<typeof createMarkdownDiff>) {
  return diff.lines
    .map((line) => {
      const className =
        line.type === 'added'
          ? 'tool-markdown-diff-line tool-markdown-diff-line-added'
          : line.type === 'removed'
            ? 'tool-markdown-diff-line tool-markdown-diff-line-removed'
            : 'tool-markdown-diff-line';
      const content = line.chunks
        .map((chunk) => {
          const chunkClassName =
            chunk.type === 'added'
              ? 'tool-markdown-diff-chunk tool-markdown-diff-chunk-added'
              : chunk.type === 'removed'
                ? 'tool-markdown-diff-chunk tool-markdown-diff-chunk-removed'
                : 'tool-markdown-diff-chunk';
          return `<span class="${chunkClassName}">${escapeHtml(chunk.text).replace(/\n/g, '<br />')}</span>`;
        })
        .join('');
      return `<div class="${className}">${content || '&nbsp;'}</div>`;
    })
    .join('');
}

export async function createHtmlPreview(html: string) {
  return sanitizeHtml(html);
}

export function createJsonDownloadText(value: unknown) {
  return JSON.stringify(value, null, 2);
}
